import { VcsLocale } from "@/locales/localeid";
import { VersionGroupModule } from "@/modules/versiongroup";
import {
	IVersionGroupService,
	UserVcsSettingsSaveModel,
	VersionGroupModel,
	VersionGroupRequestModel,
	VersionGroupService
} from "@/services/version-group";
import { AccessLevel, ContentStatus, VcsMenuCategory, VcsMenuCategoryPermission } from "@/util/enum";
import { Alert, Button, Collapse, FormModel, Icon, Input, Modal, Radio, Select } from "ant-design-vue";
import { fmtMsg, GLGlobal, isGuid, MessageHelper, NotificationType, CurriculumType } from "commonui";
import { isNull, isUndefined } from "lodash";
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import AppSettings from "@/modules/app-settings";
import { disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";

Vue.use(Modal); // to remove error '[Vue warn]: Failed to resolve directive: ant-portal (found in <Anonymous>)'

interface CreateFormModel {
	versionSelected: string;
	versionName: string;
}

@Component({
	components: {
		Icon,
		Button,
		Select,
		SelectOption: Select.Option,
		Modal,
		Form: FormModel,
		FormItem: FormModel.Item,
		Input,
		Radio,
		RadioGroup: Radio.Group,
		Collapse,
		CollapsePanel: Collapse.Panel,
		Alert
	}
})
export default class VersionManagement extends Vue {
	@Prop({ default: false }) canManage: boolean | undefined;
	public state = VersionGroupModule;
	private versionService: IVersionGroupService = VersionGroupService();
	public appSetting = getModule(AppSettings) as AppSettings;

	//** Create version properties */
	private noneOption: VersionGroupModel = {
		id: "-1",
		name: fmtMsg(VcsLocale.VersionGroupSelectNone),
		curriculumType: -1,
		isPublished: false,
		contentVersionStatusId: ContentStatus.Copied
	};
	private gsVersionTemp: string = "";
	private lsVersionTemp: string = "";
	public form: CreateFormModel = {
		versionSelected: "",
		versionName: ""
	};
	public rules = {
		versionName: [
			{ min: 2, message: fmtMsg(VcsLocale.VersionGroupAddNameLength), trigger: "change" },
			{ max: 50, message: fmtMsg(VcsLocale.VersionGroupAddNameLength), trigger: "change" },
			{ validator: this.validateVersionName, trigger: "change" }
		]
	};
	public labelCol = { span: 10 };
	public wrapperCol = { span: 14 };
	public createModalButtonLoading = false;
	public createVersionModalVisible = false;
	public isVersionValid = false;
	public isVersionUnique = true;

	//* Locale helpers
	public locale = VcsLocale;
	public format = (id: string, params?: {}) => fmtMsg(id, params);

	//* accordian properties
	public activeKey = "0";

	public applyBtnDisabled = true; // apply button enable disable while changing version

	//* Publish action properties
	public publishVersionModalVisible = false;
	public versionGroupToShow = "0"; // Radio button while publishing 0-Grape 1- littleseed
	public versionSelectedToPublish = ""; // version selected by user to publish
	public publishingVersion = false;

	// version names on which user is working
	selectedGsVersionName = "";
	selectedLsVersionName = "";

	public accessLevel = AccessLevel;

	mounted() {
		const versions = VersionGroupModule?.versionGroups;

		this.selectedGsVersionName =
			versions && isGuid(VersionGroupModule.selectedGsVersion)
				? (versions.find((v) => v.id === VersionGroupModule?.selectedGsVersion)?.name as string)
				: fmtMsg(VcsLocale.VersionGroupSelectNone);
		this.selectedLsVersionName =
			versions && isGuid(VersionGroupModule.selectedLsVersion)
				? (versions.find((v) => v.id === VersionGroupModule?.selectedLsVersion)?.name as string)
				: fmtMsg(VcsLocale.VersionGroupSelectNone);
	}

	@Watch("state.disabled")
	disableChange() {
		if (this.state.disabled) {
			this.activeKey = "0";
		}
	}

	@Watch("appSetting.childMenuSelected")
	@Watch("state.selectedGsVersion")
	@Watch("state.selectedLsVersion")
	initVersionGroup() {
		this.gsVersionTemp = VersionGroupModule?.selectedGsVersion;
		this.lsVersionTemp = VersionGroupModule?.selectedLsVersion;
	}

	get versionLoading() {
		return VersionGroupModule?.loading;
	}

	get versionAvailableToPublish() {
		const versions = VersionGroupModule.versionGroups;
		let ver: VersionGroupModel[] = [];
		if (this.versionGroupToShow == "0") {
			ver = versions.filter((v) => !v.isPublished && v.curriculumType === CurriculumType.GrapeSEED);
		} else if (this.versionGroupToShow == "1") {
			ver = versions.filter((v) => !v.isPublished && v.curriculumType === CurriculumType.LittleSEED);
		}
		if (ver.length) {
			this.versionSelectedToPublish = ver[0].id;
		} else {
			this.versionSelectedToPublish = "";
		}
		return ver;
	}

	get versionManagementAccessLevel() {
		const permissions = GLGlobal.permissionInfo();
		if (Array.isArray(permissions) && permissions.length) {
			const versionPerm = permissions.find((f) => f.id === VcsMenuCategory.ListManagement);
			if (versionPerm) {
				const versionPermCat = versionPerm.items?.find(
					(f) => f.id === VcsMenuCategoryPermission.VersionManager
				);
				if (versionPermCat) {
					return versionPermCat.accessLevel;
				}
			}
		}
		return AccessLevel.None;
	}

	// Set the name of grapeseed and selected versions in get methods used as value for select
	grapeSeedSelected() {
		const versions = this.gsVersionGroups;
		const selected = VersionGroupModule?.selectedGsVersion;
		if (versions.length) {
			const selectedVersionInfo = versions.find((v) => v.id === selected);
			this.selectedGsVersionName = selectedVersionInfo
				? selectedVersionInfo.name
				: fmtMsg(VcsLocale.VersionGroupSelectNone);
		}
		return selected;
	}

	littleSeedSelected() {
		const versions = this.lsVersionGroups;
		const selected = VersionGroupModule?.selectedLsVersion;
		if (versions.length) {
			const selectedVersionInfo = versions.find((v) => v.id === selected);
			this.selectedLsVersionName = selectedVersionInfo
				? selectedVersionInfo.name
				: fmtMsg(VcsLocale.VersionGroupSelectNone);
		}
		return selected;
	}

	// Gs And Ls version available for selection on change dropdown
	get gsVersionGroups() {
		const versions = VersionGroupModule.versionGroups;
		// disable mask here
		if (Array.isArray(versions)) {
			const grapeseedVersion = versions.filter((v) => v.curriculumType === CurriculumType.GrapeSEED);
			grapeseedVersion.unshift(this.noneOption);
			return grapeseedVersion;
		}
		return versions;
	}

	get lsVersionGroups() {
		const versions = VersionGroupModule.versionGroups;
		if (Array.isArray(versions)) {
			const littleseedVersions = versions.filter((v) => v.curriculumType === CurriculumType.LittleSEED);
			littleseedVersions.unshift({ ...this.noneOption });
			return littleseedVersions;
		}
		return versions;
	}

	get gsVersionGroupsForCreate() {
		const versions = VersionGroupModule.versionGroups;
		return versions.filter((v) => v.curriculumType === CurriculumType.GrapeSEED);
	}

	get lsVersionGroupsForCreate() {
		const versions = VersionGroupModule.versionGroups;
		return versions.filter((v) => v.curriculumType === CurriculumType.LittleSEED);
	}

	public collapseChanged() {
		this.activeKey = this.activeKey === "0" ? "1" : "0";
	}

	public onGVersionChange(id: string) {
		this.gsVersionTemp = id;
		this.applyBtnDisabled = false;
	}

	public onLVersionChange(id: string) {
		this.lsVersionTemp = id;
		this.applyBtnDisabled = false;
	}

	getSelectedVersionDetails(versionId: string) {
		const availableVersions = VersionGroupModule.versionGroups;
		return availableVersions.find((v) => v.id === versionId);
	}

	public onApply() {
		const saveToDbObj: UserVcsSettingsSaveModel = {
			selectedGSVersion: VersionGroupModule?.selectedGsVersion,
			selectedLSVersion: VersionGroupModule?.selectedLsVersion
		};

		let flag = false;
		if (this.lsVersionTemp.length) {
			const selectedLsVersionDetails = this.getSelectedVersionDetails(this.lsVersionTemp);
			if (!isUndefined(selectedLsVersionDetails) && !isNull(selectedLsVersionDetails)) {
				if (selectedLsVersionDetails.contentVersionStatusId === ContentStatus.Failed) {
					this.showFailedCopyMessage(VcsLocale.VersionGroupApplyCopyFailed, selectedLsVersionDetails.name);
					flag = true;
				} else if (selectedLsVersionDetails.contentVersionStatusId === ContentStatus.New) {
					this.showInprogressMessage(
						VcsLocale.VersionGroupApplyCopyInprogress,
						selectedLsVersionDetails.name
					);
					flag = true;
				}
			}
		}

		if (this.gsVersionTemp.length) {
			const selectedGsVersionDetails = this.getSelectedVersionDetails(this.gsVersionTemp);
			if (!isUndefined(selectedGsVersionDetails) && !isNull(selectedGsVersionDetails)) {
				if (selectedGsVersionDetails.contentVersionStatusId === ContentStatus.Failed) {
					this.showFailedCopyMessage(VcsLocale.VersionGroupApplyCopyFailed, selectedGsVersionDetails.name);
					flag = true;
				} else if (selectedGsVersionDetails.contentVersionStatusId === ContentStatus.New) {
					this.showInprogressMessage(
						VcsLocale.VersionGroupApplyCopyInprogress,
						selectedGsVersionDetails.name
					);
					flag = true;
				}
			}
		}

		if (flag) {
			return;
		}

		if (this.gsVersionTemp.length) {
			saveToDbObj.selectedGSVersion = isGuid(this.gsVersionTemp) ? this.gsVersionTemp : null;
			VersionGroupModule.setGsVersion(this.gsVersionTemp.toString());
			this.gsVersionTemp = "";
		} else {
			saveToDbObj.selectedGSVersion = isGuid(this.grapeSeedSelected()) ? this.grapeSeedSelected() : null;
		}

		if (this.lsVersionTemp.length) {
			saveToDbObj.selectedLSVersion = isGuid(this.lsVersionTemp) ? this.lsVersionTemp : null;
			VersionGroupModule.setLsVersion(this.lsVersionTemp.toString());
			this.lsVersionTemp = "";
		} else {
			saveToDbObj.selectedLSVersion = isGuid(this.littleSeedSelected()) ? this.littleSeedSelected() : null;
		}

		this.applyBtnDisabled = true;
		this.collapseChanged();

		// save to user settings
		VersionGroupService().saveVersionGroupSelected(saveToDbObj);
	}

	/// *** Create Version Modal Functions start *** /
	private creatingVersion = CurriculumType.GrapeSEED;

	public showCreateModal() {
		this.form.versionSelected = this.gsVersionGroupsForCreate.length ? this.gsVersionGroupsForCreate[0].id : "";
		this.creatingVersion = CurriculumType.GrapeSEED;

		this.createVersionModalVisible = true;
	}

	createVersionChanged() {
		if (this.creatingVersion === CurriculumType.GrapeSEED) {
			this.form.versionSelected = this.gsVersionGroupsForCreate.length ? this.gsVersionGroupsForCreate[0].id : "";
		} else {
			this.form.versionSelected = this.gsVersionGroupsForCreate.length ? this.lsVersionGroupsForCreate[0].id : "";
		}
		this.form.versionName = "";
		const formRef = this.$refs.createForm as FormModel;
		formRef.clearValidate([]);
	}

	onValidated(propName: string, passed: boolean, message: string) {
		this.isVersionValid = passed;
	}

	public validateVersionName(rule, value, callback) {
		const versions = VersionGroupModule.versionGroups;
		// check unique name
		const isPresent = versions.find((v) => v.name.trim().toLowerCase() === value.trim().toLowerCase());
		if (isPresent) {
			this.isVersionUnique = false;
			callback(fmtMsg(VcsLocale.VersionGroupAddNameUnique));
			return;
		}
		this.isVersionUnique = true;
		callback();
	}

	public onCreateModalClose() {
		const formRef = this.$refs.createForm as FormModel;
		formRef.clearValidate([]);
		formRef.resetFields();
		this.createVersionModalVisible = false;
		this.createModalButtonLoading = false;
		this.isVersionUnique = true;
		this.isVersionValid = false;
	}

	public onCreateFromSubmit(e: Event) {
		e.preventDefault();
		const formRef = this.$refs.createForm as FormModel;

		formRef.validate((valid) => {
			if (valid) {
				// create modal
				const versions: VersionGroupRequestModel[] = [];
				const model = formRef.model as CreateFormModel;
				const parentVersion = this.getSelectedVersionDetails(model.versionSelected);
				if (!isUndefined(parentVersion) && !isNull(parentVersion)) {
					if (parentVersion.contentVersionStatusId === ContentStatus.Failed) {
						this.showFailedCopyMessage(VcsLocale.VersionGroupCreateCopyFailed, parentVersion.name);
						return;
					} else if (parentVersion.contentVersionStatusId === ContentStatus.New) {
						this.showInprogressMessage(VcsLocale.VersionGroupCreateCopyInprogress, parentVersion.name);
						return;
					}
					this.createModalButtonLoading = true;

					if (model.versionName.trim().length) {
						versions.push({
							name: model.versionName.trim(),
							parentVersionGroupId: model.versionSelected,
							curriculumType: this.creatingVersion
						});
					}
					VersionGroupModule.createVersion(versions);
					this.onCreateModalClose();
				}
			}
		});
	}

	// **** Create Version Modal Finished **** /

	// ****  Publish Version Modal Start **** /

	public showPublishModal() {
		this.publishVersionModalVisible = true;
	}

	public onPublishModalclose() {
		setTimeout(() => (this.versionGroupToShow = "0"), 200);
		this.publishingVersion = false;
		this.publishVersionModalVisible = false;
	}

	showFailedCopyMessage(localeId: string, versionName: string) {
		MessageHelper.Message(NotificationType.Warning, fmtMsg(localeId, { name: versionName }));
	}

	showInprogressMessage(localeId: string, versionName: string) {
		MessageHelper.Message(NotificationType.Warning, fmtMsg(localeId, { name: versionName }));
	}

	public onPublishModalSubmit(e: Event) {
		e.preventDefault();
		const versions = VersionGroupModule.versionGroups;
		const versionToPublish = versions.find((v) => !v.isPublished && v.id == this.versionSelectedToPublish);
		if (isGuid(this.versionSelectedToPublish) && versionToPublish) {
			if (versionToPublish.contentVersionStatusId === ContentStatus.Failed) {
				this.showFailedCopyMessage(VcsLocale.VersionGroupCreateCopyFailed, versionToPublish.name);
				return;
			} else if (versionToPublish.contentVersionStatusId === ContentStatus.New) {
				this.showInprogressMessage(VcsLocale.VersionGroupCreateCopyInprogress, versionToPublish.name);
				return;
			}
			this.publishingVersion = true;
			this.versionService
				.publishVersion(this.versionSelectedToPublish)
				.then(() => {
					VersionGroupModule.refreshVersionGroup();
					this.onPublishModalclose();
					MessageHelper.Message(
						NotificationType.Success,
						fmtMsg(VcsLocale.VersionGroupPubSuccess, { name: versionToPublish.name })
					);
				})
				.catch(() => {
					MessageHelper.Message(
						NotificationType.Failed,
						fmtMsg(VcsLocale.VersionGroupPubFail, { name: versionToPublish.name })
					);
					this.onPublishModalclose();
				});
		}
	}
}
